import { z } from 'zod';
import { ORDER_STATUSES, PRODUCT_TYPES } from '../constants/order-states.js';
import { MACHINE_ISSUE_KINDS, MACHINE_ISSUE_STATUSES, MACHINE_KINDS, VEND_ATTEMPT_STATUSES, VENDING_MACHINE_STATUSES, } from '../constants/vend-states.js';
import { emailCodeSchema, recoveryCodeSchema } from './auth.js';
import { CAMERA_MEDIA_KINDS, MAX_CAMERA_CLIP_SECONDS } from './camera-media.js';
import { e164Schema, emailSchema, moneyCentsSchema, patchableSchema, unitIdentifierLookupSchema, usPhoneSchema, uuidSchema, } from './common.js';
import { vendingAdminCodeSchema } from './shop.js';
/** Only method actually built (D7). Kept as an enum, not a literal, so a
 *  future switch to Tap to Pay's siblings is a value, not a shape change. */
export const CARD_ENTRY_METHODS = ['tap_to_pay', 'payment_sheet', 'terminal_reader'];
/** Which edge of the checkout screen the tap-to-pay guide arrow points at
 *  (t0662) — 'center' pulses a marker mid-screen, 'hidden' shows no guide at
 *  all. Relative to the rendered screen, not the device chassis: the NFC
 *  antenna's physical position varies by tablet model and mounting and can't
 *  be detected, so staff set this once per machine from the tablet's own
 *  Machine settings screen. */
export const CARD_TAP_TARGETS = ['top', 'bottom', 'left', 'right', 'center', 'hidden'];
// -------------------------------------------------------- admin: machines
/** What a mechanical combination lock or keypad accepts: digits, optionally
 *  letters, a handful of characters. Kept loose on purpose — the lock itself
 *  is the real constraint, this only stops an obviously mistyped value. */
export const cabinetCodeSchema = z
    .string()
    .trim()
    .regex(/^[0-9A-Za-z]{3,12}$/, 'Use 3–12 letters or digits');
export const vendingMachineSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    status: z.enum(VENDING_MACHINE_STATUSES),
    locationId: uuidSchema,
    locationName: z.string(),
    tabletDeviceId: uuidSchema.nullable(),
    piDeviceId: uuidSchema.nullable(),
    tabletOnline: z.boolean(),
    piOnline: z.boolean(),
    /** Null when the Pi has never reported machine health, or is itself offline. */
    machineOnline: z.boolean().nullable(),
    alertPhone: e164Schema.nullable(),
    alertEmail: emailSchema.nullable(),
    note: z.string().nullable(),
    cardTapTarget: z.enum(CARD_TAP_TARGETS),
    kind: z.enum(MACHINE_KINDS),
    /** Cabinet only — the combination shown to a customer once they've paid.
     *  Null until staff set one; a cabinet with no code refuses checkout. */
    cabinetCode: z.string().nullable(),
    /** When the code last changed, so the back office can nag about a code
     *  that every past customer still knows. */
    cabinetCodeChangedAt: z.iso.datetime().nullable(),
    /** Cabinet only — how long a customer has to scan everything before the
     *  pickup is flagged for staff. */
    cabinetPickupTimeoutMinutes: z.number().int(),
    /** Cabinet only — whether the tablet offers typing a card number when a
     *  card can't be tapped. */
    cardEntryFallback: z.boolean(),
    /** Logo shown on the tablet's start screen (an image-library URL). Null
     *  shows no logo. */
    startScreenLogoUrl: z.string().nullable(),
    /** Headline on the tablet's start screen. Null keeps the built-in one. */
    startScreenMessage: z.string().nullable(),
    openIssueCount: z.number().int(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const createVendingMachineSchema = z.object({
    name: z.string().trim().min(1).max(120),
    /** Must already exist and be `type: 'kiosk'` — creating locations is the
     *  existing Locations page's job, not this one's. */
    locationId: uuidSchema,
    alertPhone: usPhoneSchema.nullable().default(null),
    alertEmail: emailSchema.nullable().default(null),
    note: z.string().trim().max(500).nullable().default(null),
    /** A cabinet gets a tablet only — there is no Pi to pair. */
    kind: z.enum(MACHINE_KINDS).default('vending'),
});
export const updateVendingMachineSchema = patchableSchema(z.object({
    name: z.string().trim().min(1).max(120),
    status: z.enum(VENDING_MACHINE_STATUSES),
    alertPhone: usPhoneSchema.nullable(),
    alertEmail: emailSchema.nullable(),
    note: z.string().trim().max(500).nullable(),
    cardTapTarget: z.enum(CARD_TAP_TARGETS),
    cabinetCode: cabinetCodeSchema.nullable(),
    cabinetPickupTimeoutMinutes: z.number().int().min(2).max(60),
    cardEntryFallback: z.boolean(),
    startScreenLogoUrl: z.url().max(2000).nullable(),
    startScreenMessage: z
        .string()
        .trim()
        .max(60)
        .transform((value) => (value === '' ? null : value))
        .nullable(),
}));
// ------------------------------------------------------------ admin: slots
export const vendingSlotSchema = z.object({
    id: uuidSchema,
    machineId: uuidSchema,
    code: z.string(),
    productId: uuidSchema.nullable(),
    productName: z.string().nullable(),
    /** Drives the admin UI's choice between "restock by count" and "load by
     *  IMEI" — a slot with no product assigned yet is null. */
    productType: z.enum(PRODUCT_TYPES).nullable(),
    sku: z.string().nullable(),
    /** For a serialized product this is the count of loaded units, computed on
     *  read; for a counted product it is the cached, authoritative slot count. */
    capacity: z.number().int(),
    qty: z.number().int(),
    enabled: z.boolean(),
    /** Only populated for serialized-product slots. */
    loadedUnits: z
        .array(z.object({ id: uuidSchema, imei: z.string(), loadedAt: z.iso.datetime() }))
        .default([]),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const upsertVendingSlotSchema = z.object({
    // Uppercased so "a1" and "A1" are the same slot everywhere a code is
    // written from — the admin form, and the terminal's upsert-by-code path
    // below (t0602). The unique index on (machineId, code) is case-sensitive,
    // so without this a duplicate slot could sneak in with different casing.
    code: z.string().trim().toUpperCase().min(1).max(20),
    productId: uuidSchema.nullable(),
    capacity: z.number().int().min(1).max(200).default(10),
    enabled: z.boolean().default(true),
});
export const upsertVendingSlotsSchema = z.object({
    slots: z.array(upsertVendingSlotSchema).min(1).max(100),
});
/** Create-only: a code already used on this machine is rejected (409), never
 *  silently overwritten — see `upsertVendingSlotsSchema` above for the
 *  terminal's separate upsert-by-code path, which stays upsert for it alone. */
export const createVendingSlotSchema = upsertVendingSlotSchema;
/** Edits an existing slot by id. Deliberately omits `code` — it's the
 *  machine's physical dispense address; delete and recreate to change it. */
export const updateVendingSlotSchema = patchableSchema(z.object({
    productId: uuidSchema.nullable(),
    capacity: z.number().int().min(1).max(200),
    enabled: z.boolean(),
}));
/** Counted (accessory) products only — a serialized slot's count is derived
 *  from load/unload, never set directly. Exactly one of the two fields. */
export const restockSlotSchema = z
    .object({
    qtyDelta: z.number().int().optional(),
    setQty: z.number().int().nonnegative().optional(),
})
    .refine((v) => (v.qtyDelta !== undefined) !== (v.setQty !== undefined), {
    message: 'Provide exactly one of qtyDelta or setQty',
});
export const slotUnitImeiSchema = z.object({ imei: unitIdentifierLookupSchema });
/** One device a tech could load into a serialized slot right now (t0619) —
 *  the slot's model, sellable, not already in a slot, at ANY location:
 *  loading one from elsewhere auto-transfers it here (t0604). */
export const vendingLoadableUnitSchema = z.object({
    id: uuidSchema,
    imei: z.string(),
    locationId: uuidSchema.nullable(),
    locationName: z.string().nullable(),
    /** True when loading it will move it to this machine's location. */
    willTransfer: z.boolean(),
});
export const loadableUnitsQuerySchema = z.object({
    /** Partial-IMEI filter — digits typed or scanned into the IMEI field. */
    q: z.string().trim().max(20).optional(),
});
// -------------------------------------------------- terminal: admin machine
//
// Trimmed like every other terminal response (see `vendingTerminalConfigSchema`
// below) — no device/location UUIDs, and deliberately no `status`: a tablet
// that retires its own machine would make `deviceAuth` reject that same
// tablet on its next request, locking the admin out on-site. Retiring stays a
// back-office-only action.
export const vendingTerminalMachineSettingsSchema = z.object({
    name: z.string(),
    status: z.enum(VENDING_MACHINE_STATUSES),
    locationName: z.string(),
    tabletOnline: z.boolean(),
    piOnline: z.boolean(),
    machineOnline: z.boolean().nullable(),
    openIssueCount: z.number().int(),
    alertPhone: e164Schema.nullable(),
    alertEmail: emailSchema.nullable(),
    note: z.string().nullable(),
    cardTapTarget: z.enum(CARD_TAP_TARGETS),
});
export const vendingTerminalUpdateMachineSchema = patchableSchema(z.object({
    name: z.string().trim().min(1).max(120),
    alertPhone: usPhoneSchema.nullable(),
    alertEmail: emailSchema.nullable(),
    note: z.string().trim().max(500).nullable(),
    cardTapTarget: z.enum(CARD_TAP_TARGETS),
}));
export const vendingTerminalAdminProductSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    sku: z.string(),
    type: z.enum(['stock', 'serialized']),
    priceCents: moneyCentsSchema,
    availableCount: z.number().int().nonnegative(),
});
/** A product a vending slot can hold — same shape whether it's read by the
 *  tablet (`/vending/terminal/admin/products`) or the back office
 *  (`/vending/products`); both are backed by `listVendableProducts()`. */
export const vendableProductSchema = vendingTerminalAdminProductSchema;
// ----------------------------------------------------------- admin: issues
export const machineIssueSchema = z.object({
    id: uuidSchema,
    machineId: uuidSchema,
    machineName: z.string(),
    kind: z.enum(MACHINE_ISSUE_KINDS),
    status: z.enum(MACHINE_ISSUE_STATUSES),
    orderId: uuidSchema.nullable(),
    orderNumber: z.string().nullable(),
    customerName: z.string().nullable(),
    customerPhone: z.string().nullable(),
    vendAttemptId: uuidSchema.nullable(),
    /** The IMEI this attempt dispensed or was holding, when known. */
    imei: z.string().nullable(),
    summary: z.string(),
    detail: z.string().nullable(),
    /** Only set on a customer-submitted report (kind `customer_reported`) —
     *  who to call back, distinct from `customerName`/`customerPhone` above
     *  which come from a signed-in account a walk-up guest doesn't have. */
    reporterName: z.string().nullable(),
    reporterPhone: z.string().nullable(),
    resolvedByStaffName: z.string().nullable(),
    resolvedAt: z.iso.datetime().nullable(),
    resolutionNote: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const resolveMachineIssueSchema = z.object({
    resolutionNote: z.string().trim().max(1000).nullable().default(null),
});
export const machineIssueListQuerySchema = z.object({
    status: z.enum(MACHINE_ISSUE_STATUSES).optional(),
    machineId: uuidSchema.optional(),
});
// ----------------------------------------------------- admin: vend attempts
export const vendAttemptSchema = z.object({
    id: uuidSchema,
    machineId: uuidSchema,
    orderId: uuidSchema,
    orderNumber: z.string(),
    slotCode: z.string().nullable(),
    imei: z.string().nullable(),
    status: z.enum(VEND_ATTEMPT_STATUSES),
    failureReason: z.string().nullable(),
    detail: z.string().nullable(),
    dispatchedAt: z.iso.datetime().nullable(),
    resolvedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
// ------------------------------------------------------- terminal: config
export const vendingTerminalConfigSchema = z.object({
    machineName: z.string(),
    /** Which customer flow the tablet runs — the dispensing one, or the glass
     *  cabinet's pay / unlock / scan one. */
    machineKind: z.enum(MACHINE_KINDS),
    paymentMethod: z.enum(CARD_ENTRY_METHODS),
    supportPhone: z.string().nullable(),
    /** Passed to the Terminal SDK's reader-discovery/connect calls. Null when
     *  the shop's structured address isn't configured yet — the app should
     *  treat that as "card payment unavailable" rather than crash into the SDK. */
    terminalLocationId: z.string().nullable(),
});
export const terminalConnectionTokenSchema = z.object({ secret: z.string() });
// ------------------------------------------------------ terminal: catalog
export const vendingTerminalProductSchema = z.object({
    slotId: uuidSchema,
    code: z.string(),
    productId: uuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    priceCents: moneyCentsSchema,
    /** Set whenever a sale is active on this product — always less than
     *  priceCents. The terminal app shows priceCents struck through next to
     *  this when present. */
    salePriceCents: moneyCentsSchema.nullable(),
    imageUrl: z.string().nullable(),
    /** Coarse on purpose, same reasoning as the web storefront: a customer
     *  needs to know whether they can buy it, not exactly how many are left. */
    available: z.boolean(),
    /** Total on hand across this machine's enabled slots for the product (a
     *  counted product may sit in more than one slot); 0 or 1 for a serialized
     *  product. Advisory only — it caps the quantity stepper client-side, but
     *  checkout re-validates against the live count and 409s if it fell short. */
    availableQty: z.number().int().nonnegative(),
});
export const vendingTerminalMachineSchema = z.object({
    machineName: z.string(),
    cardTapTarget: z.enum(CARD_TAP_TARGETS),
    startScreenLogoUrl: z.string().nullable(),
    startScreenMessage: z.string().nullable(),
    products: z.array(vendingTerminalProductSchema),
});
// ----------------------------------------------------------- terminal: auth
export const vendingTerminalLoginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1).max(128),
});
export const vendingTerminalSignupSchema = z.object({
    email: emailSchema,
    password: z.string().min(12).max(128),
    name: z.string().trim().min(1).max(150),
});
export const vendingTerminalAuthResultSchema = z.object({
    customerSessionToken: z.string(),
    /** True when the account was just created and has no name on file yet —
     *  the app should go straight to the name-capture screen. */
    needsName: z.boolean(),
    customer: z.object({
        id: uuidSchema,
        name: z.string().nullable(),
        email: emailSchema.nullable(),
        phone: e164Schema.nullable(),
    }),
});
export const vendingTerminalCompleteProfileSchema = z.object({
    name: z.string().trim().min(1).max(150),
});
// ------------------------------------------------------- terminal: checkout
/**
 * A vending machine dispenses with one physical motor action per sale, so a
 * multi-qty order can never be one vend attempt — `VendService` runs it as a
 * sequence, one `vend_attempts` row per unit, only ever one active at a time.
 * `qty` here is the number of units to buy of the *same* product; it is
 * rejected for a serialized product (a physical phone; DB-enforced qty=1).
 *
 * `buyerName`/`buyerPhone`/`createAccount`/`email` start optional at this
 * schema on purpose, even though the kiosk app requires them in its UI: a
 * fielded tablet not yet updated sends today's body with none of these
 * fields (kotlinx `encodeDefaults=false` — see the heartbeat schema's note
 * below), and a required field here would 400 every one of those checkouts
 * the moment this API deploys, before the fleet has a chance to update over
 * the air. Tighten this to required in a follow-up once the fleet is current.
 */
export const vendingTerminalCheckoutSchema = z
    .object({
    productId: uuidSchema,
    idempotencyKey: z.string().trim().min(8).max(100),
    qty: z.number().int().min(1).max(10).default(1),
    buyerName: z.string().trim().min(1).max(150).nullable().default(null),
    buyerPhone: usPhoneSchema.nullable().default(null),
    /** Optional: create a passwordless account for this buyer, invited by
     *  email to set a password later. Ignored when the request already
     *  carries a signed-in customer session. */
    createAccount: z.boolean().default(false),
    email: emailSchema.nullable().default(null),
})
    .refine((v) => !v.createAccount || (v.email && v.buyerName && v.buyerPhone), {
    message: 'Name, phone and email are all required to create an account',
    path: ['email'],
});
export const vendingTerminalCheckoutResultSchema = z.object({
    orderId: uuidSchema,
    orderNumber: z.string(),
    totalCents: z.number().int(),
    clientSecret: z.string(),
});
export const VEND_PROGRESS_STATES = [
    'none',
    'pending',
    'dispatched',
    'dispensed',
    'failed',
    'timed_out',
];
export const vendingTerminalOrderStatusSchema = z.object({
    orderId: uuidSchema,
    orderNumber: z.string(),
    status: z.enum(ORDER_STATUSES),
    payment: z.enum(['pending', 'paid', 'failed']),
    vend: z.enum(VEND_PROGRESS_STATES),
    /** Customer-facing copy for the current state — the app renders this
     *  verbatim rather than composing its own message per status combination. */
    message: z.string().nullable(),
    /** How many units have been physically dispensed so far, and how many the
     *  order is for. Both 1 for the common single-item purchase; an app that
     *  ignores them (a not-yet-updated tablet) still gets a correct `vend`. */
    dispensedCount: z.number().int().nonnegative(),
    totalCount: z.number().int().positive(),
});
// ---------------------------------------------------------- terminal: misc
/** Deployed builds serialize with kotlinx `encodeDefaults = false` and
 *  `explicitNulls = false`, so this default-valued field and a null battery
 *  reading are omitted from the JSON entirely, not sent as `null` — the
 *  schema has to accept their absence or every fielded tablet 400s on every
 *  heartbeat (t0595). A wrong `app` value is still rejected. */
export const vendingTerminalHeartbeatSchema = z.object({
    app: z.literal('vending-android').default('vending-android'),
    versionCode: z.number().int().nonnegative(),
    versionName: z.string().max(40),
    lockTaskActive: z.boolean(),
    batteryPct: z.number().int().min(0).max(100).nullish(),
    chargerConnected: z.boolean().nullish(),
    uptimeMs: z.number().int().nonnegative(),
    /** t0660: whether this tablet is device-owner provisioned. Nullish like
     *  the other optional fields above — a build that predates this reports
     *  nothing here, same t0595 rule (kotlinx omits absent/default fields). */
    deviceOwner: z.boolean().nullish(),
});
/** t0660: an `update` directive rides the heartbeat response whenever the
 *  server wants this tablet to install a newer build — either an admin
 *  explicitly requested it, or auto-update is on and the published release
 *  outruns what the tablet just reported. Absent means nothing to do. A
 *  build that predates this decodes the fatter response fine and ignores
 *  the extra field (kotlinx `ignoreUnknownKeys`). */
export const vendingTerminalHeartbeatResponseSchema = z.object({
    ok: z.literal(true),
    update: z.object({ versionCode: z.number().int() }).optional(),
    /** t1073: captures staff have asked this tablet for. An entry repeats on
     *  every heartbeat until it is delivered, failed or timed out, so the
     *  tablet dedupes by `captureId`. Answer each one with a multipart
     *  `POST /vending/terminal/camera-media/:captureId` (field `file`, a JPEG),
     *  or `.../:captureId/failure` if it cannot. Omitted when there are none. */
    captures: z
        .array(z.object({
        captureId: uuidSchema,
        kind: z.enum(CAMERA_MEDIA_KINDS),
        /** t1277: a glass cabinet's tablet also answers `snapshot` / `clip`
         *  from the store's IP cameras. `url` is the snapshot or RTSP address
         *  and may carry a camera login — never log it. A clip is delivered as
         *  an MP4 in the same `file` field. Absent for photo / screenshot. */
        url: z.string().max(1000).optional(),
        durationSeconds: z.number().int().min(1).max(MAX_CAMERA_CLIP_SECONDS).optional(),
    }))
        .optional(),
    /** t1073: whether the tablet may push a capture on its own when checkout
     *  fails (`POST /vending/terminal/camera-media`). `photos` photographs the
     *  customer and stays off unless the owner enables it. Absent from a
     *  server that predates this; treat absent as both false. */
    errorCapture: z.object({ screenshots: z.boolean(), photos: z.boolean() }).optional(),
    /** t1267: what a tablet needs to check the admin code with no connection,
     *  so staff can reach Wi-Fi settings on it. `set: false` means no code is
     *  set (the tablet forgets any it holds); `verifier` is absent when a code
     *  predates this and hasn't been re-saved yet. A flag rather than `null`
     *  because the tablet's JSON decoding can't tell null from absent. Absent
     *  from a server that predates this — the tablet then leaves what it holds
     *  alone. */
    adminCodeOffline: z
        .object({
        set: z.boolean(),
        setAt: z.iso.datetime().optional(),
        verifier: z.string().optional(),
        maxAttempts: z.number().int().optional(),
        lockoutMinutes: z.number().int().optional(),
    })
        .optional(),
});
// --------------------------------------------------- terminal: staff admin
export const vendingTerminalStaffLoginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1).max(200),
    /** Absent on the first call, which authenticates the password and emails a
     *  code; present on the second, which answers the challenge — either the
     *  emailed code or a recovery code. */
    emailCode: z.union([emailCodeSchema, recoveryCodeSchema]).nullable().default(null),
});
/** Shared by every way a tablet can end up with a staff session — the emailed
 *  2FA flow and the admin bypass code (t0611) alike. */
export const vendingTerminalStaffAuthenticatedSchema = z.object({
    status: z.literal('authenticated'),
    staffToken: z.string(),
    expiresAt: z.iso.datetime(),
    staffName: z.string(),
    permissions: z.array(z.string()),
});
export const vendingTerminalStaffLoginResultSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('email_code_sent'), codeSent: z.boolean() }),
    vendingTerminalStaffAuthenticatedSchema,
]);
/** The admin bypass code (t0611) — one shared numeric code, set from the back
 *  office Settings page, exchanged (with the device's own token) for a
 *  session under whichever staff account Settings has configured. No email
 *  round-trip, so it still works after a forced app restart. */
export const vendingTerminalCodeLoginSchema = z.object({
    code: vendingAdminCodeSchema,
});
export const vendingTerminalCodeLoginResultSchema = vendingTerminalStaffAuthenticatedSchema;
export const vendingTerminalReportIssueSchema = z.object({
    summary: z.string().trim().min(1).max(200),
    detail: z.string().trim().max(1000).nullable().default(null),
});
/** The customer-facing "report a problem" submission — same shape as the
 *  staff version above plus optional callback details, since the reporter
 *  here is very often a walk-up guest with no account on file. */
export const vendingTerminalCustomerReportIssueSchema = z.object({
    summary: z.string().trim().min(1).max(200),
    detail: z.string().trim().max(1000).nullable().default(null),
    reporterName: z.string().trim().max(150).nullable().default(null),
    reporterPhone: usPhoneSchema.nullable().default(null),
    orderId: uuidSchema.nullable().default(null),
});
//# sourceMappingURL=vending.js.map